import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { GenericPageShimmer } from '../../loader/components/generic-page-shimmer/GenericPageShimmer';
import Activation from '../../containers/activate';
import { isLegacyActivateCurrentTeam } from '../../core/activate-legacy-teams';
import { SettingsLayout } from '../../layout/components/settings-sidebar/SettingsLayout';
import { ActivateVersionFlipNotice } from './activate-version-flip-notice/ActivateVersionFlipNotice';
import styles from './Dashboard.module.scss';
import useService from '../../injector/hooks/useService';
import { MultiRegionService } from '../../auth/services/multi-region.service';
import useTeamSettings from '../../containers/team/hooks/useTeamSettingsService';

const routers = {
  Account: lazy(() => import('../../account/components/account/Account')),

  Target: lazy(() =>
    import('../../containers/activate-target').then(module => ({ default: module.Target }))
  ),

  // Activate 2.0 target UI (forked). Legacy teams get the 1.0 `Target` above.
  TargetV2: lazy(() =>
    import('../../activate/targets/components/target-routes/TargetRoutes').then(module => ({
      default: module.TargetRoutes
    }))
  ),

  ActivateV2: lazy(() =>
    import('../../activate/shell/ActivateRoutes').then(module => ({
      default: module.ActivateRoutes
    }))
  ),

  Destination: lazy(() =>
    import('../../containers/destination/destinations-container/components/Destinations').then(
      module => ({ default: module.Destination })
    )
  ),

  EnterpriseDestination: lazy(() =>
    import('../../edge/destinations/destinations/Destinations').then(module => ({
      default: module.Destinations
    }))
  ),

  EnterprisePipeline: lazy(() =>
    import('../../edge/pipelines/Pipelines').then(module => ({
      default: module.Pipelines
    }))
  ),

  JobMonitoringDashboard: lazy(() =>
    import(
      '../../containers/job-monitoring/components/job-monitoring-dashboard/JobMonitoringDashboard'
    ).then(module => ({
      default: module.JobMonitoringDashboard
    }))
  ),

  Workspace: lazy(() =>
    import('../../containers/team/Workspace/Workspace').then(module => ({
      default: module.Workspace
    }))
  ),

  NotificationSettings: lazy(() =>
    import(
      '../../containers/notification-settings/components/notification-settings/NotificationSettings'
    ).then(module => ({
      default: module.NotificationSettings
    }))
  ),

  Payment: lazy(() =>
    import('../../containers/payment').then(module => ({ default: module.Payment }))
  ),

  SetupBilling: lazy(() =>
    import('../../containers/payment/SetupBilling').then(module => ({
      default: module.SetupBilling
    }))
  ),

  Pipeline: lazy(() =>
    import('../../containers/pipeline/components/pipeline/Pipeline').then(module => ({
      default: module.Pipeline
    }))
  ),

  Team: lazy(() => import('../../containers/team').then(module => ({ default: module.Team }))),

  TransformModel: lazy(() =>
    import('../../containers/transform-model').then(module => ({ default: module.TransformModel }))
  ),

  ConnectAIWizard: lazy(
    () => import('../../edge/pipelines/CreatePipeline/connect-ai/wizard/components/connect-ai-wizard/ConnectAIWizardWithGuard')
  ),

  Transform: lazy(() =>
    import('../../transform/Transform').then(module => ({ default: module.Transform }))
  ),

  Workflow: lazy(() =>
    import('../../containers/workflow/components/workflow/Workflow').then(module => ({
      default: module.Workflow
    }))
  ),

  AccountSuspended: lazy(() =>
    import('../../containers/team/AccountSuspended').then(module => ({
      default: module.AccountSuspended
    }))
  ),

  DownloadPage: lazy(() =>
    import('../../download-page/components/DownloadPage').then(module => ({
      default: module.DownloadPage
    }))
  ),

  EmailVerificationBlock: lazy(() =>
    import('../../auth/components/email-verification-block/EmailVerificationBlock').then(
      module => ({
        default: module.EmailVerificationBlock
      })
    )
  ),

  TrailExpired: lazy(() =>
    import('../../containers/team/TrialExpired/TrialExpiredPage').then(module => ({
      default: module.TrialExpired
    }))
  )
};

function LazyComponent({ component }) {
  const Component = routers[component];
  return (
    <Suspense fallback={<GenericPageShimmer />}>
      <Component />
    </Suspense>
  );
}

export function DashboardRoutes() {
  const multiRegionService = useService(MultiRegionService);
  const { isH2OEnabled, isPostLoadTransformationEnabled, getTeamSettings } = useTeamSettings();
  const isDpRegion = multiRegionService.getSelectedRegionValue()?.type?.toLocaleLowerCase() === 'dp';
  // The same expressions the appbar gates its links on, so these routes cannot be reached by
  // URL on a team the link is hidden for.
  const isTransformEnabled = isH2OEnabled() && isPostLoadTransformationEnabled();
  const isJobsViewEnabled = !!getTeamSettings()?.show_jobs_view;
  /*
    Legacy (sunset) teams render the frozen Activate 1.0 UI; everyone else gets Activate 2.0.

    Pinned for the session by `isLegacyActivateCurrentTeam()`, so this fork cannot disagree with the
    global-search registries — which re-read it per call — if the team flag is flipped while the tab
    is open. `ActivateVersionFlipNotice` is what asks for the reload that adopts a flip.
  */
  const isLegacyActivate = isLegacyActivateCurrentTeam();
  const activateRouteElement = (
    <ActivateVersionFlipNotice>
      {isLegacyActivate ? <Activation /> : <LazyComponent component='ActivateV2' />}
    </ActivateVersionFlipNotice>
  );
  const targetRouteElement = (
    <ActivateVersionFlipNotice>
      <LazyComponent component={isLegacyActivate ? 'Target' : 'TargetV2'} />
    </ActivateVersionFlipNotice>
  );

  return (
    <div className={styles.dashboardOutletContainer}>
      <Routes>
        <Route
          key='destination-edge'
          path='/destination/edge/*'
          element={<LazyComponent component='EnterpriseDestination' />}
        />
        <Route
          key='pipeline-edge'
          path='/pipeline/edge/*'
          element={<LazyComponent component='EnterprisePipeline' />}
        />
        <Route
          key='destination'
          path='/destination/*'
          element={<LazyComponent component='Destination' />}
        />
        <Route key='workflow' path='/workflow/*' element={isDpRegion ? <Navigate to='/pipeline' replace /> : <LazyComponent component='Workflow' />} />
        <Route key='pipeline' path='/pipeline/*' element={<LazyComponent component='Pipeline' />} />
        <Route key='model' path='/model/*' element={isDpRegion ? <Navigate to='/pipeline' replace /> : <LazyComponent component='TransformModel' />} />
        <Route
          key='connect-ai-build'
          path='/connect-ai/build'
          element={<LazyComponent component='ConnectAIWizard' />}
        />
        <Route key='activation' path='/activation/*' element={isDpRegion ? <Navigate to='/pipeline' replace /> : activateRouteElement} />
        <Route
          key='transform'
          path='/transform/*'
          element={
            isTransformEnabled && !isDpRegion ? (
              <LazyComponent component='Transform' />
            ) : (
              <Navigate to='/pipeline' replace />
            )
          }
        />
        <Route key='target' path='/target/*' element={isDpRegion ? <Navigate to='/pipeline' replace /> : targetRouteElement} />
        <Route
          key='payment-setup-billing'
          path='/payment/setup-billing'
          element={<LazyComponent component='SetupBilling' />}
        />
        {/* Account, Team, Billing and Alerts share the settings sidebar; setup-billing stays outside it. */}
        <Route key='settings' element={<SettingsLayout />}>
          <Route path='/account/*' element={<LazyComponent component='Account' />} />
          <Route path='/team/*' element={<LazyComponent component='Team' />} />
          <Route path='/payment/*' element={<LazyComponent component='Payment' />} />
          <Route path='/alerts/*' element={<LazyComponent component='NotificationSettings' />} />
        </Route>
        <Route
          key='trial-expired'
          path='/trial-expired'
          element={<LazyComponent component='TrailExpired' />}
        />
        <Route
          key='email-verification'
          path='/verify-email-block'
          element={<LazyComponent component='EmailVerificationBlock' />}
        />
        <Route
          key='download-page'
          path='/download/:feature/:filename'
          element={<LazyComponent component='DownloadPage' />}
        />
        <Route
          key='account-suspended'
          path='/account-suspended'
          element={<LazyComponent component='AccountSuspended' />}
        />
        <Route
          key='all-jobs'
          path='/all-jobs'
          element={
            isJobsViewEnabled ? (
              <LazyComponent component='JobMonitoringDashboard' />
            ) : (
              <Navigate to='/pipeline' replace />
            )
          }
        />
        <Route path='*' element={<Navigate to='/pipeline' replace />} />
      </Routes>
    </div>
  );
}
